import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  Inject,
  Renderer2,
  OnDestroy,
  ChangeDetectionStrategy
} from "@angular/core";
import { Subject, Observable, Subscription, EMPTY } from "rxjs";
import {
  switchMap,
  debounceTime,
  throttleTime,
  catchError,
} from "rxjs/operators";
import moment from "moment-timezone";
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import {
  DeviceCompanyGroup,
  IDeviceInventoryFilters,
  IDeviceTypeSummary,
  DeviceCSV,
  DeviceSorter,
  IDeviceInventory,
  DeviceSortKey,
} from "../../../models/device.model";
import { CompanyGroupLocation } from "app/models/location.model";
import { MatExpansionPanel, MatExpansionModule } from "@angular/material/expansion";
import { ApiService } from "../../../services/api.service";
import { CookieService } from "ngx-cookie-service";
import { HelperService, SortOrder } from "../../../services/helper.service";
import { DeviceBindingTimePipe, DeviceTypeCodePipe, DeviceTypePipe, HighlightSearch } from "../../../pipes/pipes";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDividerModule } from "@angular/material/divider";
import { DeviceAddInventoryComponent } from "./device-add-inventory/device-add-inventory.component";
import { CommonModule } from "@angular/common";
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { i18nPaginator } from "../new-staff/i18nPaginator";
import { Angular2Csv } from "angular2-csv";
import { FeatureBadgeComponent, RewriteFeaturePipe } from "app/components/feature-badge/feature-badge.component";
import { DeviceStatusResolverPipe } from "./device-status-resolver.pipe";
import { LocalStorageService } from "app/services/local-storage.service";
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { DeviceInformationDialogComponent, IDeviceInformationDialogData } from "./device-information/device-information-dialog/device-information-dialog.component";
import { DeviceNameDialogComponent, IDeviceNameDialogData } from "./device-name-dialog/device-name-dialog.component";

export type BindingTimeConfig = { visible: boolean; unit: "Days" | "Months" | "Years" | null; defaultTime: number | null }
type GetDeviceInventoryResponse = { pageCount: number; deviceList: any[]; numberOfDevices: number };
const CSV_HEADERS = ["CUSTOM_DEVICE_ID", "NAME", "COMPANY", "LOCATION", "ADDRESS", "POSTCODE", "POSTOFFICE", "GSM_NUMBER", "DEVICE_TYPE", "DEVICE_SOFTWARE_VERSION", "DATEADDED", "DEVICE_ADDITIONAL_INFO_SHORT", "DEVICE_INFORMATION", "STATUS"]

@Component({
    selector: "companygroup-devices",
    templateUrl: "./devices.component.html",
    styleUrls: ["./devices.component.css"],
    providers: [{ provide: MatPaginatorIntl, useClass: i18nPaginator }, DeviceStatusResolverPipe, RewriteFeaturePipe],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        CommonModule,
        MatPaginatorModule,
        DeviceAddInventoryComponent,
        FormsModule,
        ReactiveFormsModule,
        MatExpansionModule,
        MatDividerModule,
        MatCheckboxModule,
        TranslateModule,
        HighlightSearch,
        FeatureBadgeComponent,
        DeviceStatusResolverPipe,
        DeviceTypeCodePipe,
        DeviceBindingTimePipe,
        MatProgressSpinnerModule
    ]
})
export class CompanygroupDevicesComponent implements OnInit, OnDestroy {
  constructor(
    public dialog: MatDialog,
    private _formBuilder: UntypedFormBuilder,
    private api: ApiService,
    private helper: HelperService,
    private renderer: Renderer2,
    private translate: TranslateService,
    private localStorageService: LocalStorageService,
    private scrollStrategyOptions: ScrollStrategyOptions,
    private deviceStatusResolver: DeviceStatusResolverPipe,
    private rewriteFeaturePipe: RewriteFeaturePipe
  ) {
    // Check if clicked outside filter dialog to close it
    this.renderer.listen("window", "click", (e) => {
      if (this.showFilterDialog) {
        if (
          !this.toggleFilterDialog.nativeElement.contains(e.target) &&
          !this.filterMenu.nativeElement.contains(e.target) &&
          e.target.id != "menuicon"
        ) {
          this.showFilterDialog = false;

          const element = document.getElementById("tab-group");
          if (element) {
            element.style.overflow = "hidden";
          }
        }
      }
    });

    // Cancel previous http request if a new device filter or sort order is selected
    this.output = this.getDeviceInventorySubject$.pipe(
      debounceTime(300),
      switchMap(() => {
        this.loadDevicesError = false;

        // Catch inside the switchMap: an error reaching the outer subscription would
        // terminate it and leave every later search, filter and page change dead
        return this.api.getCompanyGroupDeviceInventory(this.selectedFilters).pipe(
          catchError((error) => {
            console.error(error);
            this.loadDevicesError = true;
            this.pageLoaded = true;

            // Don't leave the previous results on screen under the new search term
            this.dataSource = [];
            this.companyGroupDevices = [];
            this.companyGroupDevicesAll = [];
            this.numberOfDevices = 0;
            this.pageCount = 0;

            return EMPTY;
          })
        );
      })
    );
    this.output.subscribe(
      (res: GetDeviceInventoryResponse) => {
        this.parsePageData(res);

        if (this.selectedFilters.sortKey === DeviceSorter.Status && this.selectedFilters.sortBy !== SortOrder.Default) {
          this.dataSource = this.sortByDeviceStatus(this.dataSource);
        }

        this.companyGroupDevicesAll = [];
        this.pageCountMax = 0;
        this.deviceFilter = this.selectedFilters.search.toLowerCase();
        this.pageLoaded = true;
      },
      (error) => {
        console.log(error);
        this.pageLoaded = true;
      }
    );
  }

  private getDeviceInventorySubject = new Subject<void>();
  getDeviceInventorySubject$ = this.getDeviceInventorySubject.asObservable();
  output: Observable<any>;

  locationForm: UntypedFormGroup;

  @Input()
  customerSelectionData: any;

  @Input() locationList: any;

  companyGroupLocations: CompanyGroupLocation[] = [];
  companyGroupDevices: DeviceCompanyGroup[] = [];
  companyGroupDevicesAll: DeviceCompanyGroup[] = [];

  @ViewChild("toggleFilterDialog") toggleFilterDialog: ElementRef;
  @ViewChild("filterMenu") filterMenu: ElementRef;

  @ViewChild(MatExpansionPanel, { static: true })
  matExpansionPanel: MatExpansionPanel;
  @ViewChild("deviceSearchInput", { static: true })
  deviceSearchInput: ElementRef;

  _deviceFilter: string;
  filteredDevices: DeviceCompanyGroup[] = [];
  devTypeSummary: IDeviceTypeSummary[];

  showFilterDialog: boolean = false;
  showFilterDialogAllowed: boolean = true;

  bindingTimeConfig: BindingTimeConfig = {
    visible: false, // Default value for binding time visibility is hidden
    unit: null,
    defaultTime: null,
  };

  formSubmitSubject = new Subject<Event>();
  private languageSubscription: Subscription;

  filterDialog() {
    this.showFilterDialog = !this.showFilterDialog;

    const element = document.getElementById("tab-group");
    if (element) {
      element.style.overflow = this.showFilterDialog ? "visible" : "hidden";
    }
  }

  oldSearchText = "";

  get deviceFilter(): string {
    return this._deviceFilter;
  }

  set deviceFilter(value: string) {
    this._deviceFilter = value;
    if (value.length === 0) {
      this.dataSource = this.companyGroupDevicesAll.length ? this.companyGroupDevicesAll : this.companyGroupDevices;
      if (this.pageCountMax > 0) this.pageCount = this.pageCountMax;

    } else {
      if (!this.companyGroupDevicesAll.length) {
        this.companyGroupDevicesAll = this.companyGroupDevices;
        this.pageCountMax = this.pageCount;
      }

      this.filteredDevices = this.transform(this.companyGroupDevices, value);
      this.dataSource = this.filteredDevices;
    }
  }

  transform(items: any[], searchText: string): any[] {
    if (!items) {
      return [];
    }
    searchText = searchText.toLocaleLowerCase();

    this.oldSearchText = searchText;

    return items.filter((it) => {
      return (
        (typeof it.devTypeName !== "undefined"
          ? it.devTypeName.toLocaleLowerCase().includes(searchText)
          : false) ||
        (typeof it.deviceName !== "undefined"
          ? it.deviceName.toLocaleLowerCase().includes(searchText)
          : false) ||
        (typeof it.location !== "undefined"
          ? it.location.toLocaleLowerCase().includes(searchText)
          : false) ||
        (typeof it.locationAddress !== "undefined"
          ? it.locationAddress.toLocaleLowerCase().includes(searchText)
          : false) ||
        (typeof it.resident !== "undefined"
          ? it.resident.toLocaleLowerCase().includes(searchText)
          : false) ||
        (typeof it.sSnumber !== "undefined"
          ? it.sSnumber.toLocaleLowerCase().includes(searchText)
          : false) ||
        (typeof it.devId !== "undefined"
          ? it.devId.toLocaleLowerCase().includes(searchText)
          : false) ||
        (typeof it.leasingTime !== "undefined"
          ? it.leasingTime.includes(searchText)
          : false)
      );
    });
  }

  registryUpdated: boolean = false;
  registryUpdatedMany: boolean = false;
  registryUpdatedError: boolean = false;
  registryUpdatedManyError: boolean = false;

  deviceIdAdded: string = "";

  pageCount: number;
  pageCountMax: number;
  selectedPageIndex = 0;

  pageInited: boolean = false;
  pageLoaded: boolean = true;
  loadDevicesError: boolean = false;
  dataSource = null;
  originalDevices = null;

  // initial settings for the paginator
  currentPageIndex = 0;
  pageSize = 100;
  numberOfDevices = 0;

  handlePageEvent(pageEvent: PageEvent) {
    this.pageSize = pageEvent.pageSize
    this.currentPageIndex = pageEvent.pageIndex
    this.selectedFilters.search = this.deviceSearchInput.nativeElement.value.trim();
    this.selectPage(pageEvent.pageIndex)
  }

  selectPage(page: number) {
    this.pageLoaded = false;
    this.selectedFilters.pageIndex = page;
    this.selectedFilters.pageSize = this.pageSize;
    this.selectedPageIndex = page;
    this.getDeviceInventorySubject.next();
  }

  filterDevices(): void {
    this.pageLoaded = false;
    this.selectedFilters.pageIndex = 0;
    this.selectedPageIndex = 0;
    this.currentPageIndex = 0;
    this.selectedFilters.search = this.deviceSearchInput.nativeElement.value.trim();

    this.getDeviceInventorySubject.next();
  }

  search(): void {
    this.pageLoaded = false;
    this.selectedFilters.pageIndex = 0;
    this.selectedPageIndex = 0;
    this.currentPageIndex = 0;

    const search = this.deviceSearchInput.nativeElement.value.trim();
    this.deviceSearchInput.nativeElement.value = search;
    this.selectedFilters.search = search;

    this.getDeviceInventorySubject.next();
  }

  showAddProductDialog = false;

  onSelected(
    templateRef: HTMLSelectElement,
    device: DeviceCompanyGroup,
    event: Event
  ): void {
    event.stopPropagation();

    // Get templates value
    const selectedLocationId = templateRef.value;

    // Create deep copy from selected device
    const deviceCopy: DeviceCompanyGroup = JSON.parse(JSON.stringify(device));
    deviceCopy.leasingTime = typeof deviceCopy.leasingTime === 'undefined' ? moment(new Date()).format() : deviceCopy.leasingTime;

    if (selectedLocationId == "null") {
      this.api.addDeviceToCustomerInventory(deviceCopy).subscribe((res) => {

      });
      return;
    }

    // Device is moved from Everon/customer inventory into a company location, ask for a name first.
    // Devices which are already in a location keep their current name and are moved without asking.
    if (!device.locationId) {
      this.askDeviceName(device).subscribe((name: string) => {
        // Cancelling the dialog cancels the whole move
        if (name === null || typeof name === 'undefined') {
          this.resetLocationSelection(templateRef, device);
          return;
        }

        deviceCopy.deviceName = name;
        this.moveDeviceToLocation(templateRef, device, deviceCopy, selectedLocationId);
      });
      return;
    }

    this.moveDeviceToLocation(templateRef, device, deviceCopy, selectedLocationId);
  }

  // Ask a name for the device which is being taken into use in a company location
  private askDeviceName(device: DeviceCompanyGroup): Observable<string> {
    const data: IDeviceNameDialogData = { deviceId: device.devId };

    return this.dialog.open(DeviceNameDialogComponent, {
      width: "500px",
      disableClose: true,
      autoFocus: true,
      data: data,
      scrollStrategy: this.scrollStrategyOptions.block(),
    }).afterClosed();
  }

  private moveDeviceToLocation(
    templateRef: HTMLSelectElement,
    device: DeviceCompanyGroup,
    deviceCopy: DeviceCompanyGroup,
    selectedLocationId: string
  ): void {

    // Check if the selected location's company ID is different to device's current
    const selectedCompanyId = this.getCompanyData(selectedLocationId).companyID;
    //const companyLocationChanged = selectedCompanyId !== device.companyId ? true : false;

    // Temp
    const companyLocationChanged = false;

    // Set selected values
    deviceCopy.locationId = selectedLocationId;
    deviceCopy.companyId = selectedCompanyId;
    deviceCopy.devType = device.devType;

    this.api
      .updateBaseDeviceLocation(deviceCopy, companyLocationChanged)
      .subscribe(
        (updatedDevice: DeviceCompanyGroup) => {
          // If location update was success, update location information in table row
          if (updatedDevice) {
            const index = this.filteredDevices.findIndex(
              (x) => x.devId === updatedDevice.devId
            );
            updatedDevice.devTypeName = device.devTypeName;
            this.filteredDevices[index] = updatedDevice;
            this.dataSource = this.filteredDevices;
          } else {
            alert("Failed to update device location");
            this.resetLocationSelection(templateRef, device);
          }
        },
        (error) => {
          console.log(error);
          alert("Failed to update device location");
          this.resetLocationSelection(templateRef, device);
        }
      );
  }

  // Set the location dropdown back to the position where the device currently is
  private resetLocationSelection(templateRef: HTMLSelectElement, device: DeviceCompanyGroup): void {
    if (device.locationId) {
      templateRef.value = device.locationId;
      return;
    }

    // Everon inventory and customer inventory options share the same value, so they are selected by index
    templateRef.selectedIndex = device.statusInEveronStock ? 0 : 1;
  }

  getCompanyData(locationId: string): CompanyGroupLocation {
    return this.companyGroupLocations.find((e) => e.locationID === locationId);
  }

  removeDuplicateLocation(locationsList: any[], deviceLocation: string): any {
    return locationsList?.filter((e) => e.locationName !== deviceLocation);
  }

  isDeviceBasestation(device: any): boolean {
    // Get device type from valid property key
    const devType = device.devTypeCode ?? device.devType;
    return devType.substring(0, 2) === "31" ? true : false;
  }

  openAddProductDialog() {
    this.showAddProductDialog = true;
  }

  closeAddBaseForm(): void {
    this.showAddProductDialog = false;
  }

  headerInfo = [
    {
      headName: "DEVICE_TYPE",
      sorter: DeviceSorter.DeviceType,
      class: "DEVICE_TYPE", // class name for the style
    },
    {
      headName: "DEVICE_NAME",
      sorter: DeviceSorter.DeviceName,
      class: "DEVICE_NAME",
    },
    {
      headName: "SERIAL_NUMBER",
      sorter: DeviceSorter.DeviceId,
      // Urgent and bad solution to fix table width issue when binding time is hidden
      class: this.bindingTimeConfig.visible ? "SERIAL_NUMBER" : "SERIAL_NUMBER_NOBINDING",
    },
    {
      headName: "LOCATION",
      sorter: DeviceSorter.Location,
      class: "LOCATION",
    },
    {
      headName: "ADDRESS",
      sorter: DeviceSorter.Address,
      class: "ADDRESS",
    },
    {
      headName: "RESIDENT",
      sorter: DeviceSorter.Resident,
      class: "RESIDENT",
    },
    {
      headName: "SSNUMBER",
      sorter: DeviceSorter.SSnumber,
      class: "SSNUMBER",
    },
    {
      headName: "BINDING_TIME",
      sorter: DeviceSorter.BindingTime,
      class: "BINDING_TIME",
    },
    {
      headName: "STATUS",
      sorter: DeviceSorter.Status,
      class: "STATUS",
      sortDisabled: true
    }
  ];

  getDeviceType(type: string): string {
    return this.helper.getDeviceType(type);
  }

  getLocationName(id: string): string {
    return (
      this.companyGroupLocations.find((x) => x.locationID === id)
        .locationName ?? id
    );
  }

  isLocationFilterChecked(id: string, $event: any): void {
    const checked = $event.checked;
    if (checked) {
      this.selectedFilters.locations.push(id);
    } else {
      this.selectedFilters.locations = this.selectedFilters.locations.filter(
        (str) => str !== id
      );
    }
    this.filterDevices();
  }

  isDeviceFilterChecked(id: string, $event: any): void {
    const checked = $event.checked;
    if (checked) {
      this.selectedFilters.deviceTypes.push(id);
    } else {
      this.selectedFilters.deviceTypes =
        this.selectedFilters.deviceTypes.filter((str) => str !== id);
    }
    this.filterDevices();
  }

  filterPanelSearch: string = '';

  get filteredDeviceTypeFilters(): string[] {
    if (!this.filterPanelSearch) return this.deviceTypeFilters;
    const q = this.filterPanelSearch.toLowerCase();
    return this.deviceTypeFilters.filter(dt => this.getDeviceType(dt).toLowerCase().includes(q));
  }

  get filteredLocationFilters(): string[] {
    if (!this.filterPanelSearch) return this.locationFilters;
    const q = this.filterPanelSearch.toLowerCase();
    return this.locationFilters.filter(loc => this.getLocationName(loc).toLowerCase().includes(q));
  }

  clearFilters(): void {
    this.filterPanelSearch = '';
    this.selectedFilters.search = '';
    this.deviceSearchInput.nativeElement.value = '';
    this.deviceFilter = '';
    this.selectedFilters.deviceTypes = [];
    this.selectedFilters.locations = [];
    this.selectedFilters.statusOnline = false;
    this.selectedFilters.statusOfflineUnder24h = false;
    this.selectedFilters.statusOfflineOver24h = false;
    this.selectedFilters.statusLowBattery = false;
    this.selectedFilters.statusInCustomerStock = false;
    this.selectedFilters.statusInEveronStock = false;
    this.selectedFilters.statusInService = false;
    this.selectedFilters.statusLost = false;
    this.filterDevices();
  }

  get hasActiveFilters(): boolean {
    const f = this.selectedFilters;
    return !!(
      f.deviceTypes.length || f.locations.length ||
      f.statusOnline || f.statusOfflineUnder24h || f.statusOfflineOver24h ||
      f.statusLowBattery || f.statusInCustomerStock || f.statusInEveronStock ||
      f.statusInService || f.statusLost
    );
  }

  formData: FormData = new FormData();
  uploadDeviceInventoryCsvFile: UntypedFormGroup;

  createUploadInventoryCsvFile(): void {
    this.uploadDeviceInventoryCsvFile = this._formBuilder.group({
      file: [null, Validators.required],
    });
  }

  CSVToUpload: File | null = null;

  handleCSVInput(e) {
    this.CSVToUpload = e.target.files[0];
  }

  uploadCSVFile(e: Event) {
    e.preventDefault();
    const formData = new FormData();
    // console.log(this.CSVToUpload);
    formData.append("file", this.CSVToUpload);
    // console.log(formData);
    this.api.uploadDeviceInventoryCsv(formData).subscribe((res) => {
      this.devicesAdded(res);
    });
  }

  async loadDevices(): Promise<void> {
    this.pageLoaded = false;
    this.api.getCompanyGroupLocations().subscribe((res) => {
      this.companyGroupLocations = res.sort((a, b) => a.locationName.localeCompare(b.locationName, localStorage.getItem("language")));
      // Collect locations for the filtering
      this.locationFilters = this.companyGroupLocations?.map(
        (x) => x.locationID
      );
    });
    await this.initCompanyGroupDeviceInventory();
    this.pageInited = true;
  }

  async initCompanyGroupDeviceInventory(): Promise<void> {
    await new Promise<void>((r) => {
      this.selectedFilters.language = localStorage.getItem("language");
      this.api.getCompanyGroupDeviceInventory(this.selectedFilters).subscribe(
        (res: GetDeviceInventoryResponse) => {
          this.loadDevicesError = false;
          this.parsePageData(res);
          r();
        },
        (err) => {
          console.error(err);
          this.loadDevicesError = true;
          // Resolve anyway, otherwise the caller's await never returns and the
          // page stays on "Loading..." with no way to recover
          r();
        }
      );
    });
  }

  parsePageData(data: GetDeviceInventoryResponse): void {
    this.numberOfDevices = data.numberOfDevices;
    this.companyGroupDevices = data.deviceList
    this.pageCount = data.pageCount || 1;
    this.filteredDevices = this.companyGroupDevices;
    this.dataSource = this.filteredDevices;
    this.originalDevices = [...this.dataSource];
    this.pageLoaded = true;
  }

  deviceTypeFilters: string[];
  locationFilters: string[];

  selectedFilters: IDeviceInventoryFilters = {
    pageSize: 100,
    pageIndex: 0,
    search: "",
    language: localStorage.getItem("language"),
    deviceTypes: [],
    locations: [],
    statusOnline: false,
    statusOfflineUnder24h: false,
    statusOfflineOver24h: false,
    statusLowBattery: false,
    statusInCustomerStock: false,
    statusInEveronStock: false,
    statusInService: false,
    statusLost: false,
    sortKey: DeviceSorter.DeviceType,
    sortBy: SortOrder.Default,
  };

  downloadCsv() {
    this.selectedFilters.pageSize = this.numberOfDevices;
    this.api.getCompanyGroupDeviceInventory(this.selectedFilters).subscribe((res: { pageCount: number; deviceList: IDeviceInventory[]; numberOfDevices: number }) => {
      const deviceCSV: DeviceCSV[] = [];

      res.deviceList.forEach((r) => {
        const statusText = this.translateDeviceStatus(r);
        const adds = r.locationAddress.split(' ');
        let city = adds[adds.length - 1];
        let postalCode = adds[adds.length - 2];

        city = (typeof city === 'undefined') ? "" : city;
        postalCode = (typeof postalCode === 'undefined') ? "" : postalCode;
        const leasingTime = r.leasingTime;
        const dateStr = typeof r.leasingTime === 'undefined' ? "undefined" : (leasingTime.replace('T', ' ')).substring(0, leasingTime.length - 1);

        deviceCSV.push({
          id: `="${r.devId}"`,
          name: r.deviceName,
          company: r.company,
          location: r.location,
          address: r.locationAddress,
          postalCode: `="${postalCode}"`,
          city: city,
          gsm: `="${r.gsmNumber}"`,
          productType: this.helper.getDeviceType(r.devType),
          softwareVersion: r.swrel,
          dateAdded: `="${dateStr}"`,
          additionalInfo: /^[0-9]*$/.test(r.additionalInfo) ? `="${r.additionalInfo}"` : `${r.additionalInfo}`,
          deviceInfo: /^[0-9]*$/.test(r.deviceInfo) ? `="${r.deviceInfo}"` : `${r.deviceInfo}`,
          status: statusText,
        });
      });

      const filename = "devices-" + moment().format("YYYY-MM-DD");
      const options = {
        fieldSeparator: ";",
        quoteStrings: '"',
        decimalseparator: ".",
        showLabels: true,
        showTitle: false,
        headers: this.getCsvHeaderTranslations(),
      };

      new Angular2Csv(deviceCSV, filename, options);
    });
  }

  getBindingTimeConfig(): void {
    // Check if binding time is visibility to determine if the binding time column should be shown
    this.api.getCompanyGroupBindingTimeConfig().subscribe((res) => {
      this.bindingTimeConfig = res;

      // Remove binding table header if it is not visible
      if (!this.bindingTimeConfig.visible) {
        this.headerInfo = this.headerInfo.filter((x) => x.headName !== "BINDING_TIME");
      }
    },
      error => {
        console.log(error);
        this.bindingTimeConfig.visible = false;

        if (!this.bindingTimeConfig.visible) {
          this.headerInfo = this.headerInfo.filter((x) => x.headName !== "BINDING_TIME");
        }
      });
  }

  ngOnInit(): void {
    this.getBindingTimeConfig();

    this.loadDevices();

    this.languageSubscription = this.localStorageService.currentLanguage.subscribe((language) => {
      this.loadDevices();
    });

    this.api.getCompanyGroupInventorySummary().subscribe(
      (res: IDeviceTypeSummary[]) => {
        this.devTypeSummary = [];
        this.devTypeSummary = res;
        // Collect device types for filtering and
        // Sort devicetype-filter in alphabetical order
        this.deviceTypeFilters = JSON.parse(localStorage.getItem("devType"))
          .sort((a, b) => a.deviceTypeName.localeCompare(b.deviceTypeName, localStorage.getItem("language")))
          .filter((x) => this.devTypeSummary
            .map((x) => x.deviceType).includes(x.deviceType))
          .map((x) => x.deviceType);
      },
      (error) => {
        this.devTypeSummary = [];
      }
    );

    this.createUploadInventoryCsvFile();

    // Initialize form submit subject with 3 second throttle time to prevent multiple submits
    this.formSubmitSubject
      .pipe(throttleTime(3000))
      .subscribe((event: Event) => {
        this.uploadCSVFile(event);
      });
  }

  ngOnDestroy(): void {
    this.formSubmitSubject.unsubscribe();
    this.getDeviceInventorySubject.unsubscribe();
    this.languageSubscription.unsubscribe();
  }

  openDialog(device: DeviceCompanyGroup): void {
    const data: IDeviceInformationDialogData = {
      device: device,
      bindingTimeVisible: this.bindingTimeConfig.visible,
    }

    this.dialog.open(DeviceInformationDialogComponent, {
      width: "900px",
      maxHeight: "90vh",
      disableClose: true,
      autoFocus: false,
      data: data,
      scrollStrategy: this.scrollStrategyOptions.block(),
      position: { top: '5%' },
    });
  }

  sort(sorter: DeviceSorter) {
    if (typeof sorter === "undefined") {
      return
    }

    // Reset the sort order cycle if the selected key is changed
    if (sorter !== this.selectedFilters.sortKey) { // i e if the user clicks on a different column
      this.selectedFilters.sortBy = SortOrder.Default;
    }
    this.selectedFilters.sortKey = sorter;

    // make one step forward through the sort order cycle
    this.updateSortOrder();

    if (this.selectedFilters.sortKey === DeviceSorter.Status) {
      // If sort order is default, return the original unsorted devices
      if (this.selectedFilters.sortBy === SortOrder.Default) {
        this.dataSource = [...this.originalDevices];
      } else {
        this.dataSource = this.sortByDeviceStatus(this.dataSource);
      }
      return;
    }

    this.pageLoaded = false;
    this.getDeviceInventorySubject.next();
  }

  sortByDeviceStatus(unsortedDevices: DeviceCompanyGroup[]): DeviceCompanyGroup[] {
    const statusResolver = new DeviceStatusResolverPipe();

    const sortedDevices = unsortedDevices.sort((a, b) => {
      const statusA = DeviceSortKey[statusResolver.transform(a)];
      const statusB = DeviceSortKey[statusResolver.transform(b)];

      if (statusA < statusB) {
        return this.selectedFilters.sortBy === SortOrder.Asc ? -1 : 1;
      } else if (statusA > statusB) {
        return this.selectedFilters.sortBy === SortOrder.Asc ? 1 : -1;
      } else {
        return;
      }
    });
    return sortedDevices;
  }

  deviceAdded(id: string) {
    this.registryUpdated = true;
    this.deviceIdAdded = id;
    console.log(id);
    setTimeout(() => (this.registryUpdated = false), 3000);
  }

  devsAddedToReg = 0;

  devicesAdded(count: number) {
    if (!isNaN(count)) {
      this.registryUpdatedMany = true;
    }
    this.devsAddedToReg = count;
    setTimeout(() => (this.registryUpdatedMany = false), 3000);
  }

  deviceAddedError() {
    this.registryUpdatedError = true;
  }

  devicesAddedError() {
    this.registryUpdatedManyError = true;
  }

  // Helper functions
  private updateSortOrder() {
    switch (this.selectedFilters.sortBy) {
      case SortOrder.Default:
        this.selectedFilters.sortBy = SortOrder.Asc;  // was 0 becomes 1
        break;
      case SortOrder.Asc:
        this.selectedFilters.sortBy = SortOrder.Desc; // was 1 becomes -1
        break;
      case SortOrder.Desc:
        this.selectedFilters.sortBy = SortOrder.Default; // was -1 becomes 0
        break;
    }
  }

  private translateDeviceStatus(device: IDeviceInventory | DeviceCompanyGroup): string {
    const status = this.deviceStatusResolver.transform(device as DeviceCompanyGroup);
    const translationKey = this.rewriteFeaturePipe.transform(status);
    return translationKey ? this.translate.instant(translationKey) : "";
  }

  private getCsvHeaderTranslations(): string[] {
    const headers = [];
    const translations = this.translate.instant(CSV_HEADERS);
    Object.entries(translations).forEach(([_, value]) => headers.push(value));
    return headers;
  }
}

@Component({
    selector: "moreinformation",
    templateUrl: "./moreinformation.component.html",
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [TranslateModule]
})
export class MoreDeviceInformationDialog implements OnInit {
  constructor(
    private api: ApiService,
    private cookies: CookieService,
    public dialogRef: MatDialogRef<MoreDeviceInformationDialog>,
    @Inject(MAT_DIALOG_DATA) public data: DeviceCompanyGroup
  ) { }

  histories: any[] = [];

  ngOnInit(): void {
    this.api.getDeviceInventoryMovementHistory(this.data.devId).subscribe(
      (res) => {
        this.histories = res;
      },
      (err) => {
        console.log(err);
      }
    );
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
